import { z } from '../../../zod';
import { StripeAmountValidationSchema } from '../../../shared/stripeAmount';
import { PAGE_TYPE_ENUM, StripePaymentMethodEnumValidationSchema } from '../../enum';

const buildPageFeeBaseSchema = () => {
  return z.object({
    name: z.string().openapi({
      description: 'Fee label shown at checkout.',
      example: 'Processing fee',
    }),
    amount: StripeAmountValidationSchema.nullish().openapi({
      description:
        'Fixed fee amount in the page currency minor unit (e.g. cents). Set it with `percentage` to charge both as one fee.',
      example: 50,
    }),
    percentage: z.number().min(0).nullish().openapi({
      description:
        'Percentage of the checkout amount after discounts, before tax. Set it with `amount` to charge the percentage plus the fixed amount as one fee.',
    }),
    multiplyByQuantity: z
      .boolean()
      .nullish()
      .openapi({
        description: `Multiplies the fixed \`amount\` by the selected quantity. Applies to \`${PAGE_TYPE_ENUM.CHECKOUT}\`. On a fee that also has a \`percentage\`, only the fixed amount is multiplied.`,
        example: true,
      }),
    multiplyByTickets: z
      .boolean()
      .nullish()
      .openapi({
        description: `Multiplies the fixed \`amount\` by the number of tickets. Applies to \`${PAGE_TYPE_ENUM.EVENT}\`. On a fee that also has a \`percentage\`, only the fixed amount is multiplied.`,
        example: true,
      }),
    applyToSpecificPaymentMethods: z.boolean().nullish().openapi({
      description: 'Whether this fee applies only to selected `paymentMethods`.',
      example: false,
    }),
    paymentMethods: z.array(StripePaymentMethodEnumValidationSchema).nullish().openapi({
      description:
        'Payment methods this fee applies to. Applied only when `applyToSpecificPaymentMethods` is true.',
    }),
  });
};

export const PageFeeResponseValidationSchema = buildPageFeeBaseSchema().partial().meta({
  id: 'PageFeeResponse',
  description: 'A fee as returned in the checkout page response.',
});

export const PageFeeInputValidationSchema = buildPageFeeBaseSchema().meta({
  id: 'PageFeeInput',
  example: [
    {
      name: 'Processing fee',
      amount: 100,
      percentage: null,
      multiplyByQuantity: true,
      multiplyByTickets: false,
      applyToSpecificPaymentMethods: true,
      paymentMethods: ['afterpay_clearpay', 'card', 'affirm', 'alipay', 'amazon_pay'],
    },
  ],
});
